import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Button, Spinner, Text, mergeClasses } from '@fluentui/react-components';
import { usePolicyViewerStyles } from './PolicyViewer.styles';
import { PolicyBreadcrumb } from '../PolicyBreadcrumb';
import { createPolicyApiClient } from '../../services/api/policyApiClient';
import { useImpersonationLink } from '../../services/hooks/useImpersonationLink';
import { appInsights } from '../../telemetry/appInsights';
import { normalizeHeadingLevels } from '../../utils/normalizeHeadingLevels';
import type { PolicyArtifact } from '../../types/policy';

/** Hosts whose pages may be framed. Anything else is rejected so a bad metadata record cannot frame arbitrary content. */
const ALLOWED_EXTERNAL_HOSTS = ['microsoft.com', 'microsoftonline.com'];

function toAllowedExternalUrl(rawUrl: string | undefined): string | null {
    if (!rawUrl) {
        return null;
    }
    try {
        const url = new URL(rawUrl);
        if (url.protocol !== 'https:') {
            return null;
        }
        const host = url.hostname.toLowerCase();
        const allowed = ALLOWED_EXTERNAL_HOSTS.some(
            (candidate) => host === candidate || host.endsWith(`.${candidate}`)
        );
        return allowed ? url.toString() : null;
    } catch {
        return null;
    }
}

/** How the document is delivered to the iframe. */
type FrameContent =
    | { kind: 'external'; src: string }
    | { kind: 'html'; html: string }
    | { kind: 'binary'; src: string };

/** Records a successful policy open so document usage can be reported per candidate. */
function trackPolicyOpened(artifact: PolicyArtifact, deliveryKind: FrameContent['kind'], userId: string): void {
    appInsights.trackEvent({
        name: 'PolicyOpened',
        properties: {
            userId,
            policyId: artifact.id,
            policyTitle: artifact.title,
            policyCategory: artifact.category,
            policyVersion: artifact.version,
            contentType: artifact.contentType,
            deliveryKind,
        },
    });
}

export const PolicyViewer: React.FC = () => {
    const styles = usePolicyViewerStyles();
    const homeLink = useImpersonationLink('/');
    const policiesLink = useImpersonationLink('/policies');
    const { policyId = '' } = useParams<{ policyId: string }>();
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const userId = account?.localAccountId || account?.homeAccountId || '';

    const apiClient = useMemo(() => createPolicyApiClient(instance, account), [instance, account]);

    const [policy, setPolicy] = useState<PolicyArtifact | null>(null);
    const [frameContent, setFrameContent] = useState<FrameContent | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [canRetry, setCanRetry] = useState(true);

    const loadPolicy = useCallback(async () => {
        if (!policyId) {
            return;
        }
        setLoading(true);
        setError(null);
        setCanRetry(true);
        try {
            const artifact = await apiClient.getPolicy(policyId);
            setPolicy(artifact);

            if (artifact.externalUrl) {
                const externalUrl = toAllowedExternalUrl(artifact.externalUrl);
                if (!externalUrl) {
                    setError('This policy links to a location we cannot display.');
                    return;
                }
                setFrameContent({ kind: 'external', src: externalUrl });
                trackPolicyOpened(artifact, 'external', userId);
                return;
            }

            const { blob, contentType } = await apiClient.getContent(policyId);
            if (contentType === 'text/html') {
                // Served through srcdoc so the sandbox can withhold our origin from the document.
                // Its headings nest under this page's h1 (the policy title).
                setFrameContent({ kind: 'html', html: normalizeHeadingLevels(await blob.text(), 1) });
                trackPolicyOpened(artifact, 'html', userId);
            } else {
                setFrameContent({ kind: 'binary', src: URL.createObjectURL(blob) });
                trackPolicyOpened(artifact, 'binary', userId);
            }
        } catch (err) {
            // 404 means the policy was removed (or never existed); retrying cannot help.
            if ((err as { status?: number } | null)?.status === 404) {
                setError('This policy is no longer available.');
                setCanRetry(false);
            } else {
                setError('We could not open this policy. Please try again.');
            }
            console.error('Policy load error:', err);
        } finally {
            setLoading(false);
        }
    }, [apiClient, policyId, userId]);

    useEffect(() => {
        loadPolicy();
    }, [loadPolicy]);

    useEffect(() => {
        if (frameContent?.kind !== 'binary') {
            return;
        }
        const objectUrl = frameContent.src;
        return () => URL.revokeObjectURL(objectUrl);
    }, [frameContent]);

    const isPdf = policy?.contentType === 'pdf';

    return (
        <div className={styles.container}>
            <PolicyBreadcrumb
                items={[
                    { label: 'New Hire Hub', ...homeLink },
                    { label: 'HR Policies', ...policiesLink },
                ]}
                current={policy?.title}
            />

            {loading && (
                <div className={mergeClasses(styles.card, styles.stateBlock)}>
                    <Spinner size="large" label="Loading policy..." />
                </div>
            )}

            {!loading && error && (
                <div className={mergeClasses(styles.card, styles.stateBlock)}>
                    <Text>{error}</Text>
                    {canRetry ? (
                        <Button appearance="primary" onClick={loadPolicy}>Retry</Button>
                    ) : (
                        <Button
                            as="a"
                            appearance="primary"
                            href={policiesLink.href}
                            onClick={policiesLink.onClick}
                        >
                            Back to HR Policies
                        </Button>
                    )}
                </div>
            )}

            {!loading && !error && policy && (
                <>
                    <div className={mergeClasses(styles.card, styles.headerCard)}>
                        <div className={styles.headerMain}>
                            <span
                                className={mergeClasses(
                                    styles.typeBadge,
                                    isPdf ? styles.typeBadgePdf : styles.typeBadgeHtml
                                )}
                                aria-hidden="true"
                            >
                                {policy.contentType.toUpperCase()}
                            </span>
                            <div className={styles.headerText}>
                                <h1 className={styles.title}>{policy.title}</h1>
                                {policy.category && (
                                    <span className={styles.subtle}>{policy.category}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.viewerCard}>
                        {frameContent?.kind === 'external' && (
                            <iframe
                                className={styles.frame}
                                src={frameContent.src}
                                title={`${policy.title} document`}
                                referrerPolicy="no-referrer"
                            />
                        )}
                        {frameContent?.kind === 'html' && (
                            // No allow-same-origin: srcdoc content must never run against this app's origin.
                            <iframe
                                className={styles.frame}
                                srcDoc={frameContent.html}
                                title={`${policy.title} document`}
                                sandbox="allow-popups allow-popups-to-escape-sandbox"
                                referrerPolicy="no-referrer"
                            />
                        )}
                        {frameContent?.kind === 'binary' && (
                            <iframe
                                className={styles.frame}
                                src={frameContent.src}
                                title={`${policy.title} document`}
                                referrerPolicy="no-referrer"
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
